import { createContext, useContext, useState, useEffect } from 'react';

import { getUserProjects, getProjectLocations, getProjectTemplates, getFieldReports, getProjectContracts, getAppConfig, getAllProjectForms } from '../services/projectService';

import { useAuth } from './AuthContext';

import { isCostManagementProject as checkIsCostManagementProject, PROJECT_IDS } from '../config/projects';

import { shouldSkipReviewerFilter } from '../config/reviewerRules';

import { autodeskApiThrottler } from '../utils/requestThrottler';

import axios from 'axios';



const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.hocon.ai';



export const ProjectContext = createContext();



export const useProject = () => {

  const context = useContext(ProjectContext);

  if (!context) {

    throw new Error('useProject must be used within a ProjectProvider');

  }

  return context;

};



export const ProjectProvider = ({ children }) => {

  const { token, user } = useAuth();

  const [projects, setProjects] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);

  const [locations, setLocations] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState(null);

  const [templates, setTemplates] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [loading, setLoading] = useState(true);

  const [locationsLoading, setLocationsLoading] = useState(false);

  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [error, setError] = useState(null);

  const [projectPendingCounts, setProjectPendingCounts] = useState({}); // { projectId: count }

  const [config, setConfig] = useState(null);

  const [allProjectsForms, setAllProjectsForms] = useState({}); // { projectId: { inReview: [], closed: [], inProgress: [] } }

  const [inaccessibleProjects, setInaccessibleProjects] = useState(new Set()); // Projects user doesn't have access to

  const [allProjectsLoadingProgress, setAllProjectsLoadingProgress] = useState({ loaded: 0, total: 0 }); // Loading progress for All Projects



  // Use centralized cost management project check

  const isCostManagementProject = checkIsCostManagementProject;



  // Fetch configuration

  useEffect(() => {

    const fetchConfig = async () => {

      try {

        const response = await getAppConfig();

        setConfig(response.config);

      } catch (error) {

        console.error('[ProjectContext] Config error:', error);

      }

    };



    fetchConfig();

  }, []);



  // Fetch user's projects when token is available

  useEffect(() => {

    const fetchProjects = async () => {

      if (!token) {

        setProjects([]);

        setSelectedProject(null);

        setLoading(false);

        return;

      }



      setLoading(true);

      setError(null);



      try {

        const allProjects = await getUserProjects();



        // Show all projects assigned to the user

        setProjects(allProjects);



        // Auto-select "Cost Module" by default

        if (allProjects.length > 0) {

          // Check if there's a saved project in localStorage

          const savedProjectId = localStorage.getItem('selected_project_id');

          const savedProject = allProjects.find(p => p.id === savedProjectId);



          if (savedProject) {

            setSelectedProject(savedProject);

          } else {

            // Default to "Cost Module"

            setSelectedProject({ id: 'cost-module', name: 'Cost Module' });

          }

        }

      } catch (err) {

        console.error('Error fetching projects:', err);

        setError(err.message);

        setProjects([]);

        setSelectedProject(null);

      } finally {

        setLoading(false);

      }

    };



    fetchProjects();

  }, [token]);



  // Fetch pending counts for all projects (always fetch for dropdown display)

  useEffect(() => {

    const abortController = new AbortController();



    const fetchPendingCounts = async () => {

      // Always fetch counts to show in project dropdown badges

      // Note: This is needed to show badges in dropdown even when a specific project is selected



      if (projects.length === 0 || !token || !config) return;


      const counts = {};



      await Promise.all(projects.map(async (project) => {

        try {

          // Check if aborted before processing

          if (abortController.signal.aborted) {


            return;

          }



          // Skip projects that user doesn't have access to

          if (inaccessibleProjects.has(project.id)) {

            counts[project.id] = 0;

            return;

          }



          if (isCostManagementProject(project.id)) {

            // Fetch contracts (all types) for Cost Management projects

            try {

              const contracts = await getProjectContracts(project.id);



              // Check if aborted after async call

              if (abortController.signal.aborted) {

                return;

              }



              const inReview = contracts.filter(c => {

                const status = (c.status || '').toLowerCase();

                return status.includes('review') || status.includes('submitted') || status.includes('pending') || status.includes('approval');

              });



              // For pending counts, just use the total in-review count without filtering by user

              // This avoids making many pending-approvals API calls

              counts[project.id] = inReview.length;

            } catch (contractError) {

              // If 403 error, user doesn't have access - mark as inaccessible and skip future calls

              if (contractError.message?.includes('access') || contractError.statusCode === 403) {

                setInaccessibleProjects(prev => new Set(prev).add(project.id));

                counts[project.id] = 0;

                return;

              }

              throw contractError;

            }

          } else {

            // Fetch field reports for other projects (Equipment Yard) with throttling

            const templateIds = config.fuelLogTemplateIds || [config.fuelLogTemplateId];

            if (templateIds.length > 0) {

              // Fetch from all template IDs with throttling to prevent rate limits

              const requests = templateIds.map(tid =>

                () => getFieldReports(project.id, 50, 0, tid, 'inReview')

              );

              const responses = await autodeskApiThrottler.throttleAll(requests);



              // Check if aborted after async calls

              if (abortController.signal.aborted) {

                return;

              }



              const allReports = responses.filter(r => r !== null).flatMap(r => r.reports || []);

              counts[project.id] = allReports.length;

            } else {

              counts[project.id] = 0;

            }

          }

        } catch (error) {

          // Ignore abort errors

          if (error.name === 'AbortError') {

            return;

          }

          console.error(`Error fetching pending count for project ${project.id}:`, error);

          counts[project.id] = 0;

        }

      }));



      setProjectPendingCounts(counts);

    };



    fetchPendingCounts();



    // Cleanup: abort ongoing requests when projects list changes

    return () => {

      abortController.abort();

    };

  }, [projects, token, config, user]);



  // Fetch forms from all projects for "All Projects" view (only when "All Projects" or "Cost Module" is selected)

  useEffect(() => {

    const abortController = new AbortController();



    const fetchAllProjectsForms = async () => {

      // Only fetch forms when "Cost Module" is selected to avoid loading all 50+ projects

      const shouldFetchAllProjects = selectedProject?.id === 'cost-module'

      if (!shouldFetchAllProjects) {

        setAllProjectsLoadingProgress({ loaded: 0, total: 0 });

        // Clear stale data when switching away from aggregate views

        setAllProjectsForms({});

        return;

      }



      if (projects.length === 0 || !token || !config) {

        return;

      }



      // IMPORTANT: Clear old data immediately to prevent showing stale forms when switching views

      // This ensures users don't see Equipment Yard forms when switching to Cost Module

      setAllProjectsForms({});



      // Performance tracking

      const perfStart = window.performance.now();

      console.log('[ProjectContext] Starting Cost Module data load...');



      // Filter projects for Cost Module view

      const projectsToFetch = projects.filter(p => p.id !== PROJECT_IDS.EQUIPMENT_YARD && p.id !== PROJECT_IDS.HOCON_SANDBOX) // Exclude Equipment Yard and Hocon Sandbox for Cost Module

      console.log(`[ProjectContext] Loading ${projectsToFetch.length} cost management projects...`);





      // Reset progress

      setAllProjectsLoadingProgress({ loaded: 0, total: projectsToFetch.length });



      const formsData = {};



      // PROGRESSIVE LOADING: Split projects into batches of 10

      const BATCH_SIZE = 10;

      const batches = [];

      for (let i = 0; i < projectsToFetch.length; i += BATCH_SIZE) {

        batches.push(projectsToFetch.slice(i, i + BATCH_SIZE));

      }



      // Process batches sequentially

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {

        const batch = batches[batchIndex];

        const batchStart = window.performance.now();

        console.log(`[ProjectContext] Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} projects)...`);



        // Check if aborted before processing batch

        if (abortController.signal.aborted) {

          break;

        }



        // Process all projects in current batch in parallel

        await Promise.all(batch.map(async (project) => {

        try {

          // Check if aborted before processing

          if (abortController.signal.aborted) {

            return;

          }



          // Skip projects that user doesn't have access to

          if (inaccessibleProjects.has(project.id)) {

            formsData[project.id] = { inReview: [], closed: [], inProgress: [] };

            return;

          }



          if (isCostManagementProject(project.id)) {

            // For Cost Management projects, fetch all contracts (all types: Subcontract, Consulting Agreement, etc.)

            try {

              const contracts = await getProjectContracts(project.id, null, 100);



              // Check if aborted after async call

              if (abortController.signal.aborted) {

                return;

              }



            const inReview = contracts.filter(c => {

              const status = (c.status || '').toLowerCase();

              return status.includes('review') || status.includes('submitted') || status.includes('pending') || status.includes('approval');

            });



            // PERFORMANCE OPTIMIZATION: Skip user filtering in ProjectContext
            // User filtering will be done by useCostWorkflows hook when data is actually displayed
            // This eliminates hundreds of pending-approvals API calls during initial load
            //
            // Before: 30 projects × 10 contracts = 300 API calls = 24+ seconds
            // After: Just fetch contracts = 30 API calls = 2-3 seconds
            //
            // User filtering happens later in ApprovalSummary component via useCostWorkflows
            const filteredInReview = inReview



            // Only fetch and store filtered inReview forms

            formsData[project.id] = {

              inReview: filteredInReview.map(c => {
                const contractType = c.type || 'Contract';
                const isSubcontract = contractType.toLowerCase() === 'subcontract';

                return {
                  ...c,
                  projectId: project.id,
                  projectName: project.name,
                  _isContract: !isSubcontract,
                  _isSubcontract: isSubcontract,
                  _workflowType: contractType
                };
              }),

              closed: [], // Not needed - skip fetching

              inProgress: [] // Not needed - skip fetching

            };

            } catch (contractError) {

              // If 403 error, user doesn't have access - mark as inaccessible and skip

              if (contractError.message?.includes('access') || contractError.statusCode === 403) {

                setInaccessibleProjects(prev => new Set(prev).add(project.id));

                formsData[project.id] = { inReview: [], closed: [], inProgress: [] };

                return;

              }

              throw contractError;

            }

          } else if (project.id === (config?.projectIds?.HOCON_SANDBOX || '7299ebe3-4055-47bb-b238-93503020d392')) {

            // For Hocon Sandbox, fetch ACC Forms (only inReview status)

            const inReviewForms = await getAllProjectForms(project.id, 'inReview', 50);



            // Check if aborted after async call

            if (abortController.signal.aborted) {

              return;

            }



            formsData[project.id] = {

              inReview: inReviewForms.map(form => ({

                ...form,

                projectId: project.id,

                projectName: project.name,

                _isForm: true

              })),

              closed: [], // Not needed - skip fetching

              inProgress: [] // Not needed - skip fetching

            };

          } else {

            // For other projects (Equipment Yard), fetch field reports from all templates (only inReview status)

            const templateIds = config.fuelLogTemplateIds || [config.fuelLogTemplateId];

            if (templateIds.length > 0) {

              // Fetch from all template IDs with throttling to prevent rate limits



              // Only fetch inReview status - skip closed and inProgress to reduce API calls

              const requests = templateIds.map(tid =>

                () => getFieldReports(project.id, 50, 0, tid, 'inReview')

              );

              const responses = await autodeskApiThrottler.throttleAll(requests);



              // Check if aborted after async calls

              if (abortController.signal.aborted) {

                return;

              }



              const inReviewReports = responses.filter(r => r !== null).flatMap(r => r.reports || []);



              formsData[project.id] = {

                inReview: inReviewReports.map(report => ({

                  ...report,

                  projectId: project.id,

                  projectName: project.name

                })),

                closed: [], // Not needed - skip fetching

                inProgress: [] // Not needed - skip fetching

              };

            }

          }

        } catch (error) {

          // Ignore abort errors

          if (error.name === 'AbortError') {

            return;

          }

          console.error(`Error fetching forms for project ${project.id}:`, error);

          formsData[project.id] = { inReview: [], closed: [], inProgress: [] };

        }

      }));



        // INCREMENTAL UPDATE: Update state after each batch completes

        // This allows UI to show results progressively instead of waiting for all

        const loadedSoFar = (batchIndex + 1) * BATCH_SIZE;

        console.log(`[ProjectContext] Batch ${batchIndex + 1}/${batches.length} completed in ${(window.performance.now() - batchStart).toFixed(0)}ms`);

        setAllProjectsLoadingProgress({

          loaded: Math.min(loadedSoFar, projectsToFetch.length),

          total: projectsToFetch.length

        });



        // Update state with forms loaded so far

        setAllProjectsForms({ ...formsData });

      }



      // Final update - ensure we have the complete data

      if (!abortController.signal.aborted) {

        setAllProjectsForms({ ...formsData });

        setAllProjectsLoadingProgress({ loaded: projectsToFetch.length, total: projectsToFetch.length });

        const totalTime = window.performance.now() - perfStart;

        const totalForms = Object.values(formsData).reduce((sum, proj) => sum + (proj.inReview?.length || 0), 0);

        console.log(`[ProjectContext] ✓ Cost Module data loaded: ${totalForms} forms from ${projectsToFetch.length} projects in ${totalTime.toFixed(0)}ms (${(totalTime/1000).toFixed(1)}s)`);

      }

    };



    fetchAllProjectsForms();



    // Cleanup: abort ongoing requests when selectedProject changes

    return () => {

      abortController.abort();

    };

  }, [projects, token, config, selectedProject]);



  // Fetch locations when project changes

  useEffect(() => {

    const fetchLocations = async () => {

      if (!selectedProject || !token) {

        setLocations([]);

        setSelectedLocation(null);

        return;

      }



      setLocationsLoading(true);

      try {

        const projectLocations = await getProjectLocations(selectedProject.id);

        setLocations(projectLocations || []);



        // Auto-select first location or check for saved preference

        if (projectLocations && projectLocations.length > 0) {

          const savedLocationId = localStorage.getItem(`location_${selectedProject.id}`);

          const savedLocation = projectLocations.find(l => l.id === savedLocationId);



          if (savedLocation) {

            setSelectedLocation(savedLocation);

          } else {

            setSelectedLocation(null); // Allow "All Locations" by default

          }

        }

      } catch (err) {

        console.error('Error fetching locations:', err);

        setLocations([]);

        setSelectedLocation(null);

      } finally {

        setLocationsLoading(false);

      }

    };



    fetchLocations();

  }, [selectedProject, token]);



  // Fetch templates when project changes

  useEffect(() => {

    const fetchTemplates = async () => {

      if (!selectedProject || !token) {

        setTemplates([]);

        setSelectedTemplate(null);

        return;

      }



      setTemplatesLoading(true);

      try {

        const projectTemplates = await getProjectTemplates(selectedProject.id);

        setTemplates(projectTemplates || []);



        // Check for saved template preference

        if (projectTemplates && projectTemplates.length > 0) {

          const savedTemplateId = localStorage.getItem(`template_${selectedProject.id}`);

          const savedTemplate = projectTemplates.find(t => t.id === savedTemplateId);



          if (savedTemplate) {

            setSelectedTemplate(savedTemplate);

          } else {

            setSelectedTemplate(null); // Allow "All Templates" by default

          }

        }

      } catch (err) {

        console.error('Error fetching templates:', err);

        setTemplates([]);

        setSelectedTemplate(null);

      } finally {

        setTemplatesLoading(false);

      }

    };



    fetchTemplates();

  }, [selectedProject, token]);



  // Save selected project to localStorage

  const handleSelectProject = (project) => {

    setSelectedProject(project);

    if (project) {

      localStorage.setItem('selected_project_id', project.id);

    } else {

      localStorage.removeItem('selected_project_id');

    }

  };



  // Save selected location to localStorage

  const handleSelectLocation = (location) => {

    setSelectedLocation(location);

    if (selectedProject) {

      if (location) {

        localStorage.setItem(`location_${selectedProject.id}`, location.id);

      } else {

        localStorage.removeItem(`location_${selectedProject.id}`);

      }

    }

  };



  // Save selected template to localStorage

  const handleSelectTemplate = (template) => {

    setSelectedTemplate(template);

    if (selectedProject) {

      if (template) {

        localStorage.setItem(`template_${selectedProject.id}`, template.id);

      } else {

        localStorage.removeItem(`template_${selectedProject.id}`);

      }

    }

  };



  const value = {

    projects,

    selectedProject,

    selectProject: handleSelectProject,

    locations,

    selectedLocation,

    selectLocation: handleSelectLocation,

    templates,

    selectedTemplate,

    selectTemplate: handleSelectTemplate,

    loading,

    locationsLoading,

    templatesLoading,

    error,

    projectPendingCounts,

    allProjectsForms,

    allProjectsLoadingProgress, // { loaded: number, total: number }

    config,

  };



  return (

    <ProjectContext.Provider value={value}>

      {children}

    </ProjectContext.Provider>

  );

};

